import {Controller} from "@hotwired/stimulus"
import {get} from "@rails/request.js";
import flatpickr from "flatpickr";
import {Chart, LineController, LineElement, PointElement, LinearScale, CategoryScale, Legend, Tooltip} from 'chart.js'
Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Legend, Tooltip)

// Connects to data-controller="event-graph"
export default class extends Controller {
  static targets = ["canvas", "dateInput", "loading"]
  static values = {
    url: String,
    startDate: String,
    endDate: String,
  }

  connect() {
    this.startDate = this.startDateValue
    this.endDate = this.endDateValue

    const config = this.buildFlatpickrConfig();
    flatpickr(this.dateInputTarget, config);

    this.chart = null
    this.initializeChart()
    this.fetchData()
  }

  buildFlatpickrConfig() {
    return {
      mode: "range",
      altInput: true,
      altFormat: "m-d-Y",
      dateFormat: "Y-m-d",
      defaultDate: [this.startDate, this.endDate],
      minDate: this.startDateValue,
      maxDate: "today",
      onChange: this.handleDateChange.bind(this),
    }
  }

  handleDateChange(selectedDates) {
    this.startDate = this.formatDate(selectedDates[0])
    this.endDate = selectedDates[1] ? this.formatDate(selectedDates[1]) : this.formatDate(this.endDateValue)
    this.fetchData()
  }

  formatDate(date) {
    if (!date) return ''
    const d = new Date(date)
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')  // Months are 0-based
    const day = String(d.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`  // Returns YYYY-MM-DD format
  }

  initializeChart() {
    const ctx = this.canvasTarget.getContext('2d')
    this.chart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: [],
        datasets: []
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          legend: {
            position: 'top',
            labels: {
              usePointStyle: true,
              padding: 20
            }
          },
          tooltip: {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            titleColor: '#000',
            bodyColor: '#666',
            borderColor: '#ddd',
            borderWidth: 1,
            padding: 10,
            boxPadding: 5
          }
        },
        scales: {
          y: {
            beginAtZero: true,
          },
          x: {
            grid: {
              display: false
            }
          }
        }
      }
    })
  }

  async fetchData() {
    let that = this

    try {
      const response = await get(this.urlValue, {
        query: {
          start_date: that.startDate,
          end_date: that.endDate
        },
        responseKind: "json"
      });

      const data = await response.json;
      this.updateChart(data)
    } catch (error) {
      console.error('Error fetching graph data')
      if (this.hasLoadingTarget) this.loadingTarget.classList.add("is-hidden")
    }
  }

  updateChart(data) {
    if (!this.chart) return

    this.chart.data.labels = data.dates
    this.chart.data.datasets = [
      {
        label: 'Revenue($)',
        data: data.revenue,
        borderColor: '#4CAF50',
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 0
      },
      {
        label: 'Orders',
        data: data.orders,
        borderColor: '#2196F3',
        backgroundColor: 'rgba(33, 150, 243, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 0
      },
      {
        label: 'Tickets',
        data: data.tickets,
        borderColor: '#FF9800',
        backgroundColor: 'rgba(255, 152, 0, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 0
      }
    ]
    this.chart.update()
    // Clear the loading affordance once the chart has data to draw (optional
    // target — pages without a skeleton simply don't have it).
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("is-hidden")
  }
}
